import type { BiomeId } from '@/styles/theme'
import { BIOME_ORDER } from '@/styles/theme'
import { generateBiomeLevel, generateEndlessLevel, ENDLESS_INDEX_BASE, nextSeed } from './engine/generator'
import { solveLevel } from './engine/solver'

export type Difficulty = 'easy' | 'medium' | 'hard' | 'expert'

export { ENDLESS_INDEX_BASE }

export interface LevelMeta {
  index: number
  name: string
  biome: BiomeId
  difficulty: Difficulty
  grid: string[]
  rows: number
  cols: number
  stepTarget: number
  minSlides: number
  timeTargetMs: number
  minStars: number
  coinCount: number
  gemCount: number
  maxCoins: number
}

const AUTHORED_LEVELS: LevelMeta[] = [
  {
    index: 0,
    name: "First Steps",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['o-px', 'xxpx', 'e--x', 'xxxx'],
    rows: 4,
    cols: 4,
    stepTarget: 4,
    minSlides: 3,
    timeTargetMs: 21000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 1,
    name: "Around the Block",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['xxxxx', 'xegp-', 'xxxx-', 'xxxx-', 'xo-pp'],
    rows: 5,
    cols: 5,
    stepTarget: 4,
    minSlides: 3,
    timeTargetMs: 23000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 2,
    name: "Mossy Detour",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['xxxex', 'ppppx', 'oxxxx', 'xxxxx'],
    rows: 4,
    cols: 5,
    stepTarget: 4,
    minSlides: 3,
    timeTargetMs: 25000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 3,
    name: "The Long Way",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['opxx', 'xpex', 'xxxx'],
    rows: 3,
    cols: 4,
    stepTarget: 4,
    minSlides: 3,
    timeTargetMs: 21000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 4,
    name: "Fern Gully",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['xxxx', 'xxox', 'xepx', 'xpgx', 'xpxx'],
    rows: 5,
    cols: 4,
    stepTarget: 4,
    minSlides: 3,
    timeTargetMs: 23000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 5,
    name: "Rootbound",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['oxxxxx', 'pxxxxx', '--xxxx', 'xpxxxx', 'xp--pe'],
    rows: 5,
    cols: 6,
    stepTarget: 5,
    minSlides: 4,
    timeTargetMs: 28000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 6,
    name: "Sun Dapple",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['x---pe', 'x-oxxx', 'x-xxxx', 'xpxxxx', 'xxxxxx'],
    rows: 5,
    cols: 6,
    stepTarget: 3,
    minSlides: 2,
    timeTargetMs: 18000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 7,
    name: "Grove Gate",
    biome: 'forest',
    difficulty: 'easy',
    grid: ['xepg-', 'xxppo', 'xxxxx'],
    rows: 3,
    cols: 5,
    stepTarget: 3,
    minSlides: 2,
    timeTargetMs: 20000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 8,
    name: "Dripstone",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xxxxbx', 'xxxxxx', 'xpp-xx', 'x-xpxx', 'xoxpxx', 'xxxexx'],
    rows: 6,
    cols: 6,
    stepTarget: 3,
    minSlides: 3,
    timeTargetMs: 25000,
    minStars: 8,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 9,
    name: "Slate Corridor",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xxexx', 'x--bx', 'x-xox', 'x-xpx', 'xpx-x', 'x---x'],
    rows: 6,
    cols: 5,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 27000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 10,
    name: "Blind Turn",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xxxxxx', 'xo-xxx', 'xx-xpx', 'xx-gpx', 'xxxxpx', 'xxxbex'],
    rows: 6,
    cols: 6,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 26000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 11,
    name: "Echo Chamber",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xbxxxx', 'x-x--x', 'xpppox', 'xxx-xx', 'xxxpxx', 'xxxexx'],
    rows: 6,
    cols: 6,
    stepTarget: 3,
    minSlides: 3,
    timeTargetMs: 25000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 12,
    name: "Crystal Vein",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xxxbx-', 'xxxxx-', 'xxxxx-', 'xxxxxo', 'xxp--p', 'e--xxx'],
    rows: 6,
    cols: 6,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 24000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 13,
    name: "Deep Seam",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xxxxox', 'xxxx-x', 'xxxe-x', 'bxxpgp', 'xxxxp-', 'xxxxxx'],
    rows: 6,
    cols: 6,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 29000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 14,
    name: "Stalactite Run",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['-ppp-x', '-xxxpb', '-xx--e', 'oxxxxx', 'xxxxxx'],
    rows: 5,
    cols: 6,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 28000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 15,
    name: "Cavern Maze",
    biome: 'cave',
    difficulty: 'medium',
    grid: ['xxo--', 'xbxx-', 'xx-p-', 'xx-xx', 'xx-pe', 'xxxxx'],
    rows: 6,
    cols: 5,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 27000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 16,
    name: "First Ember",
    biome: 'lava',
    difficulty: 'medium',
    grid: ['xxxxxxx', 'xxxoxxx', 'x---ppe', 'xpxgxxx', 'x-x-xxx', 'x---xxx'],
    rows: 6,
    cols: 7,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 26000,
    minStars: 18,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 17,
    name: "Molten Path",
    biome: 'lava',
    difficulty: 'medium',
    grid: ['xxxxxxx', 'xl-pp-e', 'xxpxxxx', 'xx-xxxx', 'xxpolxx', 'xxxxxxx'],
    rows: 6,
    cols: 7,
    stepTarget: 3,
    minSlides: 3,
    timeTargetMs: 25000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 18,
    name: "Ash Drift",
    biome: 'lava',
    difficulty: 'medium',
    grid: ['xexx', 'x-p-', 'xx--', 'xxpo', 'xx-l', 'xx-l'],
    rows: 6,
    cols: 4,
    stepTarget: 3,
    minSlides: 3,
    timeTargetMs: 21000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 19,
    name: "Cinder Leap",
    biome: 'lava',
    difficulty: 'medium',
    grid: ['pxxxxxx', 'p-p-xxx', '-xxgoll', '-xxxxxx', 'exxxxxx', 'xxxxxxx'],
    rows: 6,
    cols: 7,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 26000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 20,
    name: "Obsidian Ridge",
    biome: 'lava',
    difficulty: 'hard',
    grid: ['x--oxxx', 'x-xxxxx', 'x-xxxlx', 'xpxxxlx', 'xpp---x', 'xxxep-x'],
    rows: 6,
    cols: 7,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 31000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 21,
    name: "Firebreak",
    biome: 'lava',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'xxxxxxo', 'xxxxxxp', 'x-----p', 'x-xxxxx', 'x-exxxx'],
    rows: 6,
    cols: 7,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 24000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 22,
    name: "Lava Lattice",
    biome: 'lava',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'p---xxx', 'gexp-px', 'xxxxxox', 'xxxxxxx'],
    rows: 5,
    cols: 7,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 32000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 23,
    name: "Caldera",
    biome: 'lava',
    difficulty: 'hard',
    grid: ['xxxxx', 'xxxox', 'xepp-', 'xxxp-', 'xxxp-'],
    rows: 5,
    cols: 5,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 28000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 24,
    name: "Frostbite",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxxxxx', 'xxxxes', 'xsxxx-', 'xs---p', 'xxpoxx', 'xxxxxx'],
    rows: 6,
    cols: 6,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 24000,
    minStars: 30,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 25,
    name: "Slick Approach",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'esxxxxo', 'xsg-pps', 'xpxxxxx', 'xsxxxxx', 'xxxxxxx'],
    rows: 6,
    cols: 7,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 26000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 26,
    name: "Glide Line",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxsppx', 'xxpxpx', 'xxexsx', 'xxxxsx', 'xxxx-x', 'o-ss-x', 'xxxxxx'],
    rows: 7,
    cols: 6,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 28000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 27,
    name: "Glacier Gate",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxxxxox', 'xxxxxsx', 'sxxxxsx', '-xxx--x', 'pp---xx', '-xxxxxx', 'exxxxxx'],
    rows: 7,
    cols: 7,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 27000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 28,
    name: "Icebound",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'xxxxxox', 'xspgssx', 'xpxxxxx', 'xpexxxx'],
    rows: 5,
    cols: 7,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 26000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 29,
    name: "Frozen Fork",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xoxxxxx', 'xsxxx-x', 'pp-spex', 's-xxxxx', 's-xxxxx', 'spxxxxx', 'xxxxxxx'],
    rows: 7,
    cols: 7,
    stepTarget: 4,
    minSlides: 4,
    timeTargetMs: 28000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 30,
    name: "Snowdrift",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'xxxxxx-', 'xxxxxxp', 'xx-s-ss', 'xx-xxx-', 'xo-xxx-', 'xxxxe-p'],
    rows: 7,
    cols: 7,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 27000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 31,
    name: "Blizzard Run",
    biome: 'ice',
    difficulty: 'hard',
    grid: ['xxxoxxx', 'xxxgxxx', 'xxxs--x', 'xxxxxsx', 'ep-xxsx', 'xx-xx-x', 'xxp-spx'],
    rows: 7,
    cols: 7,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 32000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 32,
    name: "Aurora Rise",
    biome: 'magical',
    difficulty: 'hard',
    grid: ['x-p-pexx', 'xx-xxxpp', 'xx--dk--', 'xxxxxxx-', 'xxxxxxxo', 'xxxxxxxx'],
    rows: 6,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 34000,
    minStars: 42,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 33,
    name: "Prism Path",
    biome: 'magical',
    difficulty: 'hard',
    grid: ['x---xxxx', 'ep---d-x', 'x-x-xx-x', 'xp----kx', 'x-xxxxxx', 'xoxxxxxx', 'xxxxxxxx'],
    rows: 7,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 30000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 34,
    name: "Star Weave",
    biome: 'magical',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'xxxxokx', 'xxxxx-x', 'x-exxgx', 'x-xxxpx', 'x-xxx-x', 'x-p-dpx'],
    rows: 7,
    cols: 7,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 29000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 35,
    name: "Nebula Drift",
    biome: 'magical',
    difficulty: 'hard',
    grid: ['xxxxxxxe', 'xxxxdxx-', 'xpptpt--', 'x-xxkxxx', 'x-xx-xxx', 'x-xxoxxx', 'x-----p-'],
    rows: 7,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 34000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 36,
    name: "Warp Field",
    biome: 'magical',
    difficulty: 'hard',
    grid: ['xxxxxxx', 'xx-xxxx', 'xxkxexx', 'xx-xtxx', 'xx-xtxx', 'xo-x-pp', 'xx----d'],
    rows: 7,
    cols: 7,
    stepTarget: 7,
    minSlides: 7,
    timeTargetMs: 33000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 37,
    name: "Astral Maze",
    biome: 'magical',
    difficulty: 'hard',
    grid: ['xxokxxx', 'xxx-xxx', 'xxxpxxx', 'pxx-xxx', 'tt-gd-x', '-pexxxx', 'xxxxxxx'],
    rows: 7,
    cols: 7,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 29000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 38,
    name: "Cosmic Fork",
    biome: 'magical',
    difficulty: 'expert',
    grid: ['xx--dptx', 'xx-xxx-x', 'xo-xxxpx', 'xxpxxx-x', 'xk-xxetx', 'x-xxxxxx', 'xpxxxxxx'],
    rows: 7,
    cols: 8,
    stepTarget: 7,
    minSlides: 7,
    timeTargetMs: 37000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 3,
  },
  {
    index: 39,
    name: "Void Step",
    biome: 'magical',
    difficulty: 'expert',
    grid: ['xxxtp', 'xxxt-', 'xxx--', 'x-d--', 'xkxp-', 'x-xex', 'xoxxx'],
    rows: 7,
    cols: 5,
    stepTarget: 5,
    minSlides: 5,
    timeTargetMs: 27000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 40,
    name: "Lumen Gate",
    biome: 'magical',
    difficulty: 'expert',
    grid: ['xgp---xx', 'x-xpxxxx', 'x-xpxxxx', 'xex-xxxx', 'xxx-xxxx', 'xxd-xxxx', 'xx-k---o'],
    rows: 7,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 32000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 41,
    name: "Radiance",
    biome: 'magical',
    difficulty: 'expert',
    grid: ['xxxxxxox', 'xxxxxxpx', 'xxxxxxpx', 'xx---p--', 'xx-xxx-p', 'xxkxxxx-', 'ed-xxxxx'],
    rows: 7,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 34000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 42,
    name: "Dungeon Door",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xxxxxbx', 'xxxxxxx', 'xxxxxxe', 'xxxxxxd', 'xppttx-', 'xl--k--', 'xx-xxxx', 'xo-bxxx'],
    rows: 8,
    cols: 7,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 30000,
    minStars: 56,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 43,
    name: "Torchlit",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['bxxxxxxx', 'xxxxxbxx', 'xxxp-xxx', 'xxx-pxxx', 'xxx--xxe', 'xxx--g--', 'xxxx-xxx', 'ok-dpxxx'],
    rows: 8,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 32000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 44,
    name: "Iron Corridor",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xbxxxxbx', 'txxx-xxx', '-xo-kxxx', 'p-xxpxxx', 'x-xx-xxx', 'l-xx-xxx', 'p-tpdxxx', 'xexxxxxx'],
    rows: 8,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 34000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 45,
    name: "The Vault",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xxpxxko', 'xx-xx-x', 'xx-xx-x', 'xxd--tx', 'xb-x--x', 'xxex--x', 'xxxtpbx', 'xxxxxxx'],
    rows: 8,
    cols: 7,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 30000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 46,
    name: "Chain Maze",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xxxxxxxx', 'xxxbxxxx', 'p-l-xbxx', '--xxxxxx', 'd-g-p--x', 'x-xxxxex', 'okxxxxxx', 'p-xxxxxx'],
    rows: 8,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 32000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
  {
    index: 47,
    name: "Warden Walk",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xxxexxxx', 'xxx-xxxx', 'oppkdxxx', 'xxx--xxx', 'bxb----p', 'xxxxpx-x', 'xx-----x', 'xxxxxxxx'],
    rows: 8,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 34000,
    minStars: 0,
    coinCount: 4,
    gemCount: 0,
    maxCoins: 4,
  },
  {
    index: 48,
    name: "Deep Cell",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xxxxexxx', 'xxxdp---', 'oxxx----', 'k---p-xb', 'xxxxxxxx', 'xxxxbxxx', 'xxxxxxxx'],
    rows: 7,
    cols: 8,
    stepTarget: 7,
    minSlides: 7,
    timeTargetMs: 33000,
    minStars: 0,
    coinCount: 2,
    gemCount: 0,
    maxCoins: 2,
  },
  {
    index: 49,
    name: "Keeper Gate",
    biome: 'dungeon',
    difficulty: 'expert',
    grid: ['xxbxxxxx', 'xxxxxxex', 'xxxxx--x', 'xxxxdpgx', 'kp---xxx', '-xxxxxxx', 'poxxxxxx', 'xxbxxxxx'],
    rows: 8,
    cols: 8,
    stepTarget: 6,
    minSlides: 6,
    timeTargetMs: 32000,
    minStars: 0,
    coinCount: 3,
    gemCount: 1,
    maxCoins: 4,
  },
]

export const CAMPAIGN_LEVELS_PER_BIOME = 40
export const GENERATED_CAMPAIGN_LEVEL_COUNT = CAMPAIGN_LEVELS_PER_BIOME * BIOME_ORDER.length
const INTRO_GENERATED_LEVELS = 5

function difficultyForWorldSlot(slot: number, total: number): number {
  const t = slot / Math.max(1, total - 1)
  if (slot < 3) return 1
  if (t < 0.24) return 2
  if (t < 0.46) return 3
  if (t < 0.68) return 4
  if (t < 0.86) return 5
  return 6
}

function campaignSeed(biome: BiomeId, slot: number, index: number): number {
  let seed = 0x746d6843 ^ Math.imul(index + 1, 2654435761)
  for (let i = 0; i <= BIOME_ORDER.indexOf(biome) + slot; i++) seed = nextSeed(seed)
  return seed >>> 0 || 1
}

function buildCampaignLevels(): LevelMeta[] {
  const out: LevelMeta[] = []
  let nextIndex = 0
  for (const biome of BIOME_ORDER) {
    const authored = AUTHORED_LEVELS.filter((level) => level.biome === biome).sort((a, b) => a.index - b.index)
    const gate = authored[0]?.minStars ?? 0
    const introCount = biome === 'forest' ? 0 : INTRO_GENERATED_LEVELS
    for (let slot = 0; slot < CAMPAIGN_LEVELS_PER_BIOME; slot++) {
      const authoredIndex = slot - introCount
      const raw = authoredIndex >= 0 && authoredIndex < authored.length
        ? authored[authoredIndex]
        : generateBiomeLevel(campaignSeed(biome, slot, nextIndex), biome, difficultyForWorldSlot(slot, CAMPAIGN_LEVELS_PER_BIOME))
      const level = normalizeCampaignLevel(raw, nextIndex, slot === 0 ? gate : 0)
      out.push(level)
      nextIndex++
    }
  }
  return out
}

export const LEVELS: LevelMeta[] = buildCampaignLevels()

function normalizeCampaignLevel(level: LevelMeta, index: number, minStars: number): LevelMeta {
  const solved = solveLevel(level.grid)
  if (!solved.solvable) throw new Error(`Campaign level ${level.name} is not solvable`)
  let coinCount = 0
  let gemCount = 0
  for (const row of level.grid) {
    for (const ch of row) {
      if (ch === 'p') coinCount++
      else if (ch === 'g') gemCount++
    }
  }
  return {
    ...level,
    index,
    rows: level.grid.length,
    cols: Math.max(...level.grid.map((row) => row.length)),
    minSlides: solved.minSlides,
    stepTarget: Math.max(level.stepTarget, solved.minSlides),
    minStars,
    coinCount,
    gemCount,
    maxCoins: solved.maxCoins,
  }
}

export const LEVEL_COUNT = LEVELS.length

export function getLevel(index: number): LevelMeta | undefined {
  if (index >= ENDLESS_INDEX_BASE) return getEndlessLevel(index - ENDLESS_INDEX_BASE)
  return LEVELS[index]
}

const endlessCache = new Map<number, LevelMeta>()

export function getEndlessLevel(runIndex: number): LevelMeta {
  const idx = Math.max(0, Math.floor(runIndex))
  const cached = endlessCache.get(idx)
  if (cached) return cached
  const level = generateEndlessLevel(idx)
  endlessCache.set(idx, level)
  return level
}

export function isEndlessIndex(index: number | null | undefined): boolean {
  return index != null && index >= ENDLESS_INDEX_BASE
}

export function biomeLevels(biome: BiomeId): LevelMeta[] {
  return LEVELS.filter((l) => l.biome === biome)
}
